import { forwardRef, useCallback, useSyncExternalStore } from 'react';

import { ZyDefnIcon } from '@/components/tiptap-editor/tiptap-icons/zydefn-icon';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

import { isSelectionDescendantOf } from '@/lib/zy-tiptap-utils';
import type { Editor } from '@tiptap/core';
import { GenericExtensionButton, type GenericExtensionButtonProps } from '../generic-extension-button';

export const zyDefnContent = {
    terms: ['matrix'],
    definition: ' ',
    obeycase: [true],
};

function isDefnDisabled(editor: Editor) {
    const isSelectionDescendantOfNode = ['testQuestion'].some(node => isSelectionDescendantOf(editor, node));
    if (isSelectionDescendantOfNode) {
        return false;
    }
    return true;
}

const ZyDefnButton = forwardRef<
    HTMLButtonElement,
    Omit<GenericExtensionButtonProps, 'extensionName' | 'extensionType' | 'icon' | 'label' | 'command'>
>((props, ref) => {
    const { editor } = useTiptapEditor(props.editor);

    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            if (!editor) return () => {};
            editor.on('selectionUpdate', onStoreChange);
            editor.on('update', onStoreChange);
            return () => {
                editor.off('selectionUpdate', onStoreChange);
                editor.off('update', onStoreChange);
            };
        },
        [editor],
    );

    const getSnapshot = useCallback(
        () => (editor?.isActive('zydefn') || editor?.isActive('zydefnBlock') ? 'Remove zyDefinition' : 'Insert zyDefinition'),
        [editor],
    );

    const label = useSyncExternalStore(subscribe, getSnapshot);

    return (
        <GenericExtensionButton
            id="zydefn-button"
            extensionName="zydefn"
            extensionType="node"
            icon={ZyDefnIcon}
            label={label}
            shortcutKeys="Alt+d"
            isActive={editor => editor.isActive('zydefn') || editor.isActive('zydefnBlock')}
            isDisabled={editor => !isDefnDisabled(editor)}
            command={editor => {
                if (editor.chain().focus().removeZyDefn().run()) {
                    return;
                }
                const { from, to, $from, $to, empty } = editor.state.selection;
                const selectedCopyText = empty ? '' : editor.state.doc.textBetween(from, to, '');

                if (!selectedCopyText) {
                    editor
                        .chain()
                        .focus()
                        .selectTextblockEnd()
                        .insertDefinitionWithContent({ content: [{ type: 'text', text: ' ' }] })
                        .run();
                    return;
                }

                const isSingleParentInlineSelection = $from.sameParent($to) && $from.parent.isTextblock;

                if (isSingleParentInlineSelection) {
                    const parent = $from.parent;
                    const relFrom = from - $from.start();
                    const relTo = to - $from.start();
                    const inlineFrag = parent.content.cut(relFrom, relTo);
                    const content = inlineFrag.toJSON();

                    editor
                        .chain()
                        .focus()
                        .deleteSelection()
                        .insertDefinitionWithContent({
                            content,
                        })
                        .run();
                    return;
                }

                editor.chain().focus().setZyDefnBlock().run();
            }}
            ref={ref}
            {...props}
        />
    );
});

ZyDefnButton.displayName = 'ZyDefnButton';

export default ZyDefnButton;
